import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  MapIcon,
  DocumentArrowUpIcon,
  FolderIcon,
  UserGroupIcon,
  ChartBarIcon,
  ArrowTrendingUpIcon,
  MapPinIcon
} from '@heroicons/react/24/outline';
import { getDatasets } from '../services/api';

function StatCard({ icon: Icon, label, value, trend, color = 'blue' }) {
  const colorClasses = {
    blue: 'bg-blue-100 text-blue-600',
    green: 'bg-green-100 text-green-600',
    purple: 'bg-purple-100 text-purple-600',
    amber: 'bg-amber-100 text-amber-600',
    red: 'bg-red-100 text-red-600'
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between">
        <div className={`p-3 rounded-lg ${colorClasses[color]}`}>
          <Icon className="h-6 w-6" />
        </div>
        {trend && (
          <div className="flex items-center text-sm text-green-600">
            <ArrowTrendingUpIcon className="h-4 w-4 mr-1" />
            {trend}
          </div>
        )}
      </div>
      <div className="mt-4">
        <div className="text-2xl font-bold text-gray-900">{value}</div>
        <div className="text-sm text-gray-500">{label}</div>
      </div>
    </div>
  );
}

function QuickActionCard({ icon: Icon, title, description, to, color = 'blue' }) {
  const colorClasses = {
    blue: 'bg-blue-50 hover:bg-blue-100 border-blue-200',
    green: 'bg-green-50 hover:bg-green-100 border-green-200',
    purple: 'bg-purple-50 hover:bg-purple-100 border-purple-200'
  };

  const iconColorClasses = {
    blue: 'text-blue-600',
    green: 'text-green-600',
    purple: 'text-purple-600'
  };

  return (
    <Link
      to={to}
      className={`block p-6 rounded-lg border transition-colors ${colorClasses[color]}`}
    >
      <Icon className={`h-8 w-8 ${iconColorClasses[color]}`} />
      <h3 className="mt-4 text-lg font-semibold text-gray-900">{title}</h3>
      <p className="mt-2 text-sm text-gray-600">{description}</p>
    </Link>
  );
}

function RecentDatasetItem({ dataset }) {
  return (
    <Link
      to={`/map?dataset=${dataset.id}`}
      className="flex items-center justify-between p-4 hover:bg-gray-50 rounded-lg transition-colors"
    >
      <div className="flex items-center">
        <div className="p-2 bg-gray-100 rounded-lg">
          <FolderIcon className="h-5 w-5 text-gray-600" />
        </div>
        <div className="ml-4">
          <div className="text-sm font-medium text-gray-900">{dataset.name}</div>
          <div className="text-xs text-gray-500">
            {dataset.voterCount?.toLocaleString()} voters | {dataset.geocodedPercent}% geocoded
          </div>
        </div>
      </div>
      <div className="text-xs text-gray-400">
        {new Date(dataset.createdAt).toLocaleDateString()}
      </div>
    </Link>
  );
}

export default function Dashboard() {
  const { data: datasetsData, isLoading } = useQuery({
    queryKey: ['datasets'],
    queryFn: getDatasets,
    retry: 1
  });

  const datasets = datasetsData?.datasets || [];
  const totalVoters = datasets.reduce((sum, d) => sum + (d.voterCount || 0), 0);
  const totalGeocoded = datasets.reduce((sum, d) => sum + (d.geocodedCount || 0), 0);

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Election Fabric Dashboard</h1>
        <p className="mt-1 text-gray-600">
          AI-powered voter data visualization and analysis
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          icon={FolderIcon}
          label="Datasets"
          value={datasets.length}
          color="blue"
        />
        <StatCard
          icon={UserGroupIcon}
          label="Total Voters"
          value={totalVoters.toLocaleString()}
          color="green"
        />
        <StatCard
          icon={MapPinIcon}
          label="Geocoded"
          value={totalGeocoded.toLocaleString()}
          color="purple"
        />
        <StatCard
          icon={ChartBarIcon}
          label="Geocoding Rate"
          value={totalVoters > 0 ? `${Math.round((totalGeocoded / totalVoters) * 100)}%` : '0%'}
          color="amber"
        />
      </div>

      {/* Quick Actions */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <QuickActionCard
            icon={DocumentArrowUpIcon}
            title="Import Voter Data"
            description="Upload CSV, Excel, PDF, or Word files containing voter information"
            to="/import"
            color="blue"
          />
          <QuickActionCard
            icon={MapIcon}
            title="View Voter Map"
            description="Visualize voter locations with color-coded markers"
            to="/map"
            color="green"
          />
          <QuickActionCard
            icon={FolderIcon}
            title="Manage Datasets"
            description="View, edit, and organize your imported voter datasets"
            to="/datasets"
            color="purple"
          />
        </div>
      </div>

      {/* Recent Datasets */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Recent Datasets</h2>
        </div>
        <div className="divide-y divide-gray-100">
          {isLoading ? (
            <div className="p-6 text-center text-gray-500">Loading datasets...</div>
          ) : datasets.length === 0 ? (
            <div className="p-6 text-center">
              <FolderIcon className="h-12 w-12 text-gray-300 mx-auto" />
              <p className="mt-4 text-gray-500">No datasets yet</p>
              <Link
                to="/import"
                className="mt-4 inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                <DocumentArrowUpIcon className="h-5 w-5 mr-2" />
                Import your first dataset
              </Link>
            </div>
          ) : (
            datasets.slice(0, 5).map(dataset => (
              <RecentDatasetItem key={dataset.id} dataset={dataset} />
            ))
          )}
        </div>
        {datasets.length > 5 && (
          <div className="px-6 py-3 border-t border-gray-200 bg-gray-50 rounded-b-lg">
            <Link
              to="/datasets"
              className="text-sm text-blue-600 hover:text-blue-700 font-medium"
            >
              View all datasets
            </Link>
          </div>
        )}
      </div>

      {/* Feature Overview */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-800 rounded-lg p-8 text-white">
        <h2 className="text-xl font-bold">AI-Powered Features</h2>
        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <h3 className="font-semibold">Smart Schema Detection</h3>
            <p className="mt-2 text-blue-100 text-sm">
              AI automatically detects and maps fields from your voter data files,
              even from unstructured documents.
            </p>
          </div>
          <div>
            <h3 className="font-semibold">Visualization Suggestions</h3>
            <p className="mt-2 text-blue-100 text-sm">
              Get AI-recommended color schemes and visualization strategies
              based on your data patterns.
            </p>
          </div>
          <div>
            <h3 className="font-semibold">Coming Soon: Route Optimization</h3>
            <p className="mt-2 text-blue-100 text-sm">
              Optimize door-knocking routes and identify high-value voter
              engagements automatically.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
